'use client';
import React, { useState, useEffect } from 'react';
import { SvgInfo, SvgCheck } from '../formSvg/formSvg';
import { Tooltip } from 'react-tooltip';
import { submitCeligoOptOutRequest } from '@/app/api/celigoOptOutRequest/submitCeligoOptOutRequest';
import { startUnsubscribeJob } from '@/app/api/shopify/customer/account/startUnsubscribeJob/startUnsubscribeJob';

export default function CustomerForm({ title }) {
  const [feedback, setFeedback] = useState();
  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    addr1: '',
    addr2: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [isMounted, setIsMounted] = useState(false);
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});

  const REGEX_PATTERNS = {
    email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    phone: /^\+[0-9]{1,6}?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/
  };

  const requiredFields = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'addr1',
    'city',
    'state',
    'zipCode'
  ];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validateField = (name, value) => {
    if (requiredFields.includes(name) && !value)
      return 'This field is required';
    if (name === 'email' && !REGEX_PATTERNS.email.test(value))
      return 'Invalid email format';
    if (name === 'phone' && !REGEX_PATTERNS.phone.test(value))
      return 'Invalid phone format';
    return '';
  };

  const validateAll = () => {
    const errors = {};
    requiredFields.forEach((field) => {
      const error = validateField(field, formValues[field]);
      if (error) errors[field] = error;
    });
    setErrorMessages(errors);
    setReadyToSubmit(Object.keys(errors).length === 0);
    return errors;
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    const error = validateField(name, value);
    setErrorMessages((prevErrors) => ({ ...prevErrors, [name]: error }));
    setReadyToSubmit(!error);
  };

  const unSubscribeCustomer = async (event) => {
    event.preventDefault();
    const errors = validateAll();

    if (Object.keys(errors).length === 0) {
      try {
        const optOutStatus = await submitCeligoOptOutRequest(formValues);
        if (!optOutStatus?.success) {
          setFeedback('ERROR');
        } else {
          await startUnsubscribeJob(formValues);
          setFeedback('SUCCESS');
        }
      } catch {
        console.error('Something went wrong with your opt-out request');
        setFeedback('ERROR');
      }
    }
  };

  return (
    <form
      onSubmit={unSubscribeCustomer}
      id="unsubscribe-form"
      className="mt-5 mb-4 bg-white px-8 pt-6 pb-8 shadow-md"
    >
      <div className="font-lato mx-auto flex w-full max-w-[400px] flex-col items-center lg:max-w-[600px]">
        {title && <h3 className="p-10 text-center">{title}</h3>}
        <div className="w-full lg:flex">
          <div className="mr-6 w-full pb-2">
            <label htmlFor="firstName" className="form-label">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              className="input border-parlor-green placeholder-parlor-green block w-full border bg-white px-[10px] py-2 focus:outline-none"
              name="firstName"
              value={formValues.firstName || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={200}
            />
            <div className="text-error-red h-5 text-sm">
              {errorMessages.firstName ? 'Please add a first name' : ''}
            </div>
          </div>
          <div className="w-full pb-2">
            <label htmlFor="lastName" className="form-label">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              className="input border-parlor-green placeholder-parlor-green block w-full border bg-white px-[10px] py-2 focus:outline-none"
              name="lastName"
              value={formValues.lastName || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={200}
            />
            <div className="text-error-red h-5 text-sm">
              {errorMessages.lastName ? 'Please add a last name' : ''}
            </div>
          </div>
        </div>
        <div className="w-full lg:flex">
          <div className="mr-6 w-full pb-2">
            <div className="relative">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <div
                className="mx-1 inline-block h-4 w-4"
                data-for="opt-out-email"
                data-tooltip-id="opt-out-email"
                data-tip="opt-out-email"
              >
                <SvgInfo />
              </div>
              {isMounted && (
                <Tooltip
                  id="opt-out-email"
                  className="w-60"
                  place="top"
                  effect="solid"
                  textColor="white"
                  backgroundColor="black"
                >
                  Enter an email with the format of johnsmith@email.com
                </Tooltip>
              )}
            </div>
            <input
              type="email"
              id="email"
              className="input border-parlor-green placeholder-parlor-green block w-full border bg-white px-[10px] py-2 focus:outline-none"
              name="email"
              value={formValues.email || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={200}
            />
            <div className="text-error-red h-5 text-sm">
              {errorMessages.email ? 'Please add a valid email' : ''}
            </div>
          </div>
          <div className="w-full pb-2">
            <div>
              <label htmlFor="phone" className="form-label">
                Phone Number
              </label>
              <div
                className="mx-1 inline-block h-4 w-4"
                data-for="opt-out-phone"
                data-tooltip-id="opt-out-phone"
                data-tip="opt-out-phone"
              >
                <SvgInfo />
              </div>
              {isMounted && (
                <Tooltip
                  id="opt-out-phone"
                  place="top"
                  effect="solid"
                  textColor="white"
                  backgroundColor="black"
                >
                  <span>
                    Enter a phone number with the format +, the country code,
                    the area code, and then the phone number. No spaces or
                    dashes.
                  </span>
                </Tooltip>
              )}
            </div>
            <input
              type="text"
              id="phone"
              placeholder="+12345678901"
              className="input border-parlor-green placeholder-parlor-green block w-full border bg-white px-[10px] py-2 focus:outline-none"
              name="phone"
              value={formValues.phone || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={200}
            />
            <div className="text-error-red h-5 text-sm">
              {errorMessages.phone ? 'Please add a valid phone number' : ''}
            </div>
          </div>
        </div>
        <div className="w-full lg:flex">
          <div className="mr-6 w-full pb-2">
            <label htmlFor="addr1" className="form-label">
              Address 1
            </label>
            <input
              type="text"
              id="addr1"
              className="input border-parlor-green placeholder-parlor-green block w-full border bg-white px-[10px] py-2 focus:outline-none"
              name="addr1"
              value={formValues.addr1 || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={200}
            />
            <div className="text-error-red h-5 text-sm">
              {errorMessages.addr1 ? 'Please add an address' : ''}
            </div>
          </div>
          <div className="w-full pb-4">
            <label htmlFor="addr2" className="form-label">
              Address 2 (optional)
            </label>
            <input
              type="text"
              id="addr2"
              className="input border-parlor-green placeholder-parlor-green block w-full border bg-white px-[10px] py-2 focus:outline-none"
              name="addr2"
              value={formValues.addr2 || ''}
              onChange={handleChange}
              maxLength={200}
            />
          </div>
        </div>
        <div className="w-full lg:flex">
          <div className="w-full pb-2 lg:w-1/2 lg:pr-3">
            <label htmlFor="city" className="form-label">
              City
            </label>
            <input
              type="text"
              id="city"
              className="input border-parlor-green placeholder-parlor-green block w-full border bg-white px-[10px] py-2 focus:outline-none"
              name="city"
              value={formValues.city || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={200}
            />
            <div className="text-error-red h-5 text-sm">
              {errorMessages.city ? 'Please add a city' : ''}
            </div>
          </div>
          <div className="lg:flex lg:w-1/2">
            <div className="mr-6 w-full pb-2 lg:ml-3 lg:max-w-[150px]">
              <label htmlFor="state" className="form-label">
                State
              </label>
              <select
                className="input border-parlor-green placeholder-parlor-green block h-[42px] w-full border bg-white px-[10px] py-2 focus:outline-none"
                name="state"
                id="state"
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <option value="">Select a State</option>
                <option value="AL">AL</option>
                <option value="AK">AK</option>
                <option value="AZ">AZ</option>
                <option value="AR">AR</option>
                <option value="CA">CA</option>
                <option value="CO">CO</option>
                <option value="CT">CT</option>
                <option value="DE">DE</option>
                <option value="DC">DC</option>
                <option value="FL">FL</option>
                <option value="GA">GA</option>
                <option value="HI">HI</option>
                <option value="ID">ID</option>
                <option value="IL">IL</option>
                <option value="IN">IN</option>
                <option value="IA">IA</option>
                <option value="KS">KS</option>
                <option value="KY">KY</option>
                <option value="LA">LA</option>
                <option value="ME">ME</option>
                <option value="MD">MD</option>
                <option value="MA">MA</option>
                <option value="MI">MI</option>
                <option value="MN">MN</option>
                <option value="MS">MS</option>
                <option value="MO">MO</option>
                <option value="MT">MT</option>
                <option value="NE">NE</option>
                <option value="NV">NV</option>
                <option value="NH">NH</option>
                <option value="NJ">NJ</option>
                <option value="NM">NM</option>
                <option value="NY">NY</option>
                <option value="NC">NC</option>
                <option value="ND">ND</option>
                <option value="OH">OH</option>
                <option value="OK">OK</option>
                <option value="OR">OR</option>
                <option value="PA">PA</option>
                <option value="RI">RI</option>
                <option value="SC">SC</option>
                <option value="SD">SD</option>
                <option value="TN">TN</option>
                <option value="TX">TX</option>
                <option value="UT">UT</option>
                <option value="VT">VT</option>
                <option value="VA">VA</option>
                <option value="WA">WA</option>
                <option value="WV">WV</option>
                <option value="WI">WI</option>
                <option value="WY">WY</option>
              </select>
              <div className="text-error-red h-5 text-sm">
                {errorMessages.state ? 'Please select a state' : ''}
              </div>
            </div>
            <div className="w-full pb-10">
              <label htmlFor="zipCode" className="form-label">
                Zip Code
              </label>
              <input
                type="text"
                id="zipCode"
                className="input border-parlor-green placeholder-parlor-green block w-full border bg-white px-[10px] py-2 focus:outline-none"
                name="zipCode"
                value={formValues.zipCode || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={200}
              />
              <div className="text-error-red h-5 text-sm">
                {errorMessages.zipCode ? 'Please add a zipcode' : ''}
              </div>
            </div>
          </div>
        </div>
        <button
          disabled={readyToSubmit ? false : true}
          type="submit"
          className={`btn btn-primary ${readyToSubmit ? '' : 'bg-grey-text!'}`}
        >
          Send Request
        </button>
        <div className="container mx-auto mb-4 flex w-1/2 min-w-fit flex-col px-8 pt-5">
          <FormResponse feedback={feedback} />
        </div>
      </div>
    </form>
  );
}

const FormResponse = ({ feedback }) => {
  const messageType = (() => {
    // Hard error (infrastructure / Shopify failure)
    if (feedback === 'ERROR') return 'error';

    // Already opted out (explicit state)
    if (feedback?.optedOut === true) return 'true';

    // Soft-success cases (email not found, processed, etc.)
    if (
      feedback === 'SUCCESS' ||
      feedback === 'NOT_FOUND' ||
      feedback === 'FALSE_SUCCESS'
    ) {
      return 'false';
    }

    return '';
  })();

  return (
    <div>
      {messageType == 'true' && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="bg-green relative h-6 w-6 min-w-[24px] rounded-full">
              <SvgCheck />
            </div>
            <div className="ml-3 self-center">
              <p className="text-sm font-medium">
                You are already opted out of all marketing.
              </p>
            </div>
          </div>
        </div>
      )}
      {messageType == 'false' && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="bg-green relative h-6 w-6 min-w-[24px] rounded-full">
              <SvgCheck />
            </div>
            <div className="ml-3 self-center">
              <p className="text-sm font-medium">
                Your opt out request has been received and will be processed
                within 14 days.
              </p>
            </div>
          </div>
        </div>
      )}
      {messageType == 'error' && (
        <div className="rounded-md bg-yellow-50 p-4">
          <div className="flex">
            <div className="relative h-6 w-6 min-w-[24px] rounded-full bg-yellow-400">
              <div className="w-full text-center text-yellow-50">!</div>
            </div>
            <div className="ml-3 self-center">
              <p className="text-sm font-medium">
                Something went wrong. Please try again later or contact customer
                service.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
